import {
  Box,
  Button,
  Icon,
  Text,
  Toggle,
  ToggleGroup,
  ToggleGroupItem,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
} from "@salestrak/ui";
import type { Omittable } from "@salestrak/ui";

import type { UseFiltersResult } from "#app/lib/filters/types";
import { AnalyticsContext, IssueEvent, useAnalytics } from "#app/lib/analytics";
import { useIssueFiltersOptional } from "#app/lib/filters/issue-filter-context";

/**
 * The viewer's saved filters, as a quiet toggle at the END of the header rung
 * rather than a third tab beside the editors. It is a PLACE in the panel, not
 * an editor mode, so it is the host's state rather than `filters.activeMode`
 * — selecting it leaves both editors' filters applied exactly as they were,
 * and it does not belong in the strip that picks between them.
 */
export type SavedFiltersTab = {
  selected: boolean;
  count: number;
  /** Picking the Saved tab selects it; picking Simple or Advanced leaves it,
   *  whether or not that pick changes `filters.activeMode`. */
  onSelectedChange: (selected: boolean) => void;
  /** Ask for a name and save the applied filters as a new row. */
  onSaveRequest: () => void;
};

type FilterModeTabsProps = Omittable<{
  filters: UseFiltersResult;
  saved?: SavedFiltersTab;
  /** Simple-tab values living on another layer (a routed field, see
   *  `QuickFilterLayerFor`) that this surface's rows still show — added to
   *  the tab's count so the strip agrees with the rows under it. */
  routedSimpleCount?: number;
}>;

type FilterSaveButtonProps = Omittable<{
  filters: UseFiltersResult;
  saved: SavedFiltersTab;
}>;

/**
 * Save the applied set as a saved filter. Offered wherever a filter surface
 * offers saved filters — the toolbar panel and a view's own editor — and
 * only while something is applied: an empty set is nothing to come back to.
 * It ends the header rung rather than the list, since a control that ends
 * the list moves with the list's length.
 */
export function FilterSaveButton({ filters, saved }: FilterSaveButtonProps) {
  if (filters.activeFilterCount === 0) return null;
  // No glyph: the bookmark belongs to the Saved toggle sitting beside this,
  // and two bookmarks 8px apart read as one control drawn twice.
  return (
    <Button variant="ghost" size="xs" onClick={saved.onSaveRequest}>
      <Text size="xs" variant="muted">
        Save
      </Text>
    </Button>
  );
}

/**
 * The door to the saved filters: a quiet icon toggle at the far end of the
 * header rung. The bookmark is the saved-filters glyph everywhere (the empty
 * state, the row faces), the count says how many wait behind it, and the
 * pressed fill is what says the panel is showing them — the editor tabs go
 * dark at the same moment, so the rung reads one place at a time.
 */
export function SavedFiltersToggle({ saved }: Omittable<{ saved: SavedFiltersTab }>) {
  const label = "Saved filters";
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Toggle
            size="xs"
            aria-label={label}
            pressed={saved.selected}
            onPressedChange={saved.onSelectedChange}
          />
        }
      >
        <Icon name="bookmark" size="sm" color="muted" />
        {saved.count > 0 ? (
          <Text size="xs" variant="muted">
            {saved.count}
          </Text>
        ) : null}
      </TooltipTrigger>
      <TooltipPopup side="top">{label}</TooltipPopup>
    </Tooltip>
  );
}

/**
 * The header rung a filter surface opens on: the mode tabs at the start and,
 * where the surface offers saved filters, Save and the Saved toggle at the
 * end, one row. No clear rides it: the toolbar pool clears from its chip's ×
 * and the ⌥⇧F chord, and a view's saved set comes off value by value in its
 * own lists — a clear an inch inside either surface said the same thing
 * twice. The host supplies the inset, so the rung lands on whatever edge its
 * surface reads from.
 */
export function FilterModeHeader({
  filters,
  saved,
  routedSimpleCount,
}: FilterModeTabsProps) {
  return (
    <Box direction="row" align="center" justify="between" gap="sm">
      <FilterModeTabs
        filters={filters}
        saved={saved}
        routedSimpleCount={routedSimpleCount}
      />
      {saved ? (
        <Box direction="row" align="center" gap="2xs">
          <FilterSaveButton filters={filters} saved={saved} />
          <SavedFiltersToggle saved={saved} />
        </Box>
      ) : null}
    </Box>
  );
}

/**
 * The Simple / Advanced switch every filter surface heads with — the toolbar
 * menu, the mobile filter drawer and the Customize view panel's Filters page
 * — so the two editors are picked the same way wherever filters are edited.
 *
 * The selected tab IS `filters.activeMode`. The count and the where derive from
 * both modes at once, and a feature can switch the mode programmatically, so a
 * local tab state would drift — the badge counting one mode while the strip
 * shows the other's empty tab. Each tab carries its own count for the same
 * reason: both modes apply together, so filters never "vanish" into the tab
 * you are not looking at.
 *
 * `saved` is state only here — the Saved PLACE renders as `SavedFiltersToggle`
 * on the rung's far end, never as a third tab. While it is selected neither
 * editor tab lights, and picking one tells the host to leave Saved outright:
 * the mode Saved was entered from is still the active one, so re-picking it
 * sets nothing a host could watch.
 *
 * ToggleGroup takes an array value and is single-select by default; an empty
 * next value is ignored so a tab is always selected.
 */
export function FilterModeTabs({
  filters,
  saved,
  routedSimpleCount = 0,
}: FilterModeTabsProps) {
  const { track } = useAnalytics();
  const scope = useIssueFiltersOptional()?.analyticsScope ?? "unknown";
  const count = (value: number) =>
    value > 0 ? (
      <Text size="xs" variant="muted">
        {value}
      </Text>
    ) : null;

  return (
    <ToggleGroup
      size="sm"
      value={saved?.selected ? [] : [filters.activeMode]}
      onValueChange={(next: string[]) => {
        const mode = next[0];
        if (mode === "simple" || mode === "advanced") {
          saved?.onSelectedChange(false);
          filters.setActiveMode(mode);
          track({
            context: AnalyticsContext.Issue,
            event: IssueEvent.SwitchFilterMode,
            data: { mode, scope },
          });
        }
      }}
    >
      <ToggleGroupItem value="simple">
        <Text size="sm" weight="medium">
          Simple
        </Text>
        {count(filters.simpleFilterCount + routedSimpleCount)}
      </ToggleGroupItem>
      <ToggleGroupItem value="advanced">
        <Text size="sm" weight="medium">
          Advanced
        </Text>
        {count(filters.advancedFilterCount)}
      </ToggleGroupItem>
    </ToggleGroup>
  );
}
